<template>
  <div class="payments-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <!-- Header -->
    <div class="payments-header">
      <div class="header-content">
        <div class="header-text">
          <h1 class="header-title">Payment History</h1>
          <p class="header-subtitle">Track payments and export statements</p>
        </div>
        <button class="btn-back" @click="goBack">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Back to Dashboard</span>
        </button>
      </div>
    </div>

    <v-container class="payments-container">
      <!-- KPI Cards -->
      <div class="kpi-grid">
        <div class="kpi-card kpi-paid">
          <div class="kpi-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 11L12 14L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Total Paid</div>
            <div class="kpi-value">{{ money(totalPaid) }}</div>
            <div class="kpi-meta">{{ paidCount }} payments</div>
          </div>
        </div>

        <div class="kpi-card kpi-outstanding">
          <div class="kpi-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M12 8V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3.05025 11H5.00025C7.76142 11 10.0003 13.2386 10.0003 16V16C10.0003 18.7614 12.2388 21 15.0002 21H18.0002" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3.05024 7C3.05024 6.05719 3.43944 5.15303 4.13269 4.48427C4.82594 3.8155 5.76869 3.43408 6.75024 3.43408H8.25024C9.23179 3.43408 10.1745 3.8155 10.8678 4.48427C11.561 5.15303 11.9502 6.05719 11.9502 7V16C11.9502 16.7956 12.2706 17.5587 12.8394 18.1213C13.4083 18.6839 14.1798 19 15.0002 19H16.5002C17.3207 19 18.0921 18.6839 18.661 18.1213C19.2299 17.5587 19.5502 16.7956 19.5502 16V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Outstanding</div>
            <div class="kpi-value">{{ money(totalOutstanding) }}</div>
            <div class="kpi-meta">{{ outstandingCount }} invoices</div>
          </div>
        </div>

        <div class="kpi-card kpi-last">
          <div class="kpi-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Last Payment</div>
            <div class="kpi-value-small">{{ lastPaymentDate ? formatDate(lastPaymentDate) : '—' }}</div>
            <div class="sparkline-container">
              <svg viewBox="0 0 200 40" preserveAspectRatio="none">
                <polyline
                  :points="sparklinePoints"
                  fill="none"
                  stroke="url(#sparkGradient)"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <defs>
                  <linearGradient id="sparkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style="stop-color:#B3554A;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#C89B4C;stop-opacity:1" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div class="kpi-meta">Last {{ lastAmounts.length }} amounts</div>
          </div>
        </div>
      </div>

      <!-- Payments Table Card -->
      <div class="table-card">
        <div class="table-header">
          <div class="table-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1 10H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Payment History</span>
          </div>

          <div class="table-filters">
            <div class="filter-group">
              <label>Status</label>
              <select v-model="statusFilter">
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <div class="filter-group">
              <label>Start Date</label>
              <input v-model="startDate" type="date" />
            </div>

            <div class="filter-group">
              <label>End Date</label>
              <input v-model="endDate" type="date" />
            </div>

            <button class="btn-export" @click="exportCSV">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25M12.75 6L9 2.25M9 2.25L5.25 6M9 2.25V11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Export
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="table-loading">
          <div class="loader"></div>
          <p>Loading payment history...</p>
        </div>

        <!-- Table -->
        <div v-else-if="filteredPayments.length > 0" class="payments-table">
          <div class="table-head">
            <div class="th th-amount">Amount</div>
            <div class="th th-status">Status</div>
            <div class="th th-date">Date</div>
            <div class="th th-invoice">Invoice</div>
          </div>

          <div class="table-body">
            <div v-for="payment in filteredPayments" :key="payment.id" class="table-row">
              <div class="td td-amount">
                <span class="amount-value">{{ money(payment.amount) }}</span>
              </div>

              <div class="td td-status">
                <span :class="['status-badge', 'status-' + (payment.status || 'unknown').toLowerCase()]">
                  {{ prettyStatus(payment.status) }}
                </span>
              </div>

              <div class="td td-date">
                <span class="date-value">{{ formatDate(payment.timestamp) }}</span>
              </div>

              <div class="td td-invoice">
                <a 
                  v-if="payment.invoiceUrl" 
                  :href="payment.invoiceUrl" 
                  target="_blank"
                  class="btn-invoice"
                  title="View invoice"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M10.5 2.25H4.5C4.10218 2.25 3.72064 2.40804 3.43934 2.68934C3.15804 2.97064 3 3.35218 3 3.75V14.25C3 14.6478 3.15804 15.0294 3.43934 15.3107C3.72064 15.592 4.10218 15.75 4.5 15.75H13.5C13.8978 15.75 14.2794 15.592 14.5607 15.3107C14.842 15.0294 15 14.6478 15 14.25V6.75L10.5 2.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10.5 2.25V6.75H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
                <span v-else class="no-invoice">—</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="table-empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path d="M56 16H8C5.79086 16 4 17.7909 4 20V44C4 46.2091 5.79086 48 8 48H56C58.2091 48 60 46.2091 60 44V20C60 17.7909 58.2091 16 56 16Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 26H60" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <h3>No payments yet</h3>
          <p>When invoices are generated, they will appear here</p>
        </div>

        <!-- Message Alert -->
        <div v-if="message" :class="['message-alert', messageType]">
          <svg v-if="messageType === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ message }}</span>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { auth, db } from '../firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'
import router from '../router'

export default {
  name: 'StaffPayments',
  data() {
    return {
      payments: [],
      loading: true,
      message: '',
      messageType: 'success',
      statusFilter: '',
      startDate: null,
      endDate: null
    }
  },
  computed: {
    paidOnly() {
      return this.payments.filter(p => (p.status || '').toLowerCase() === 'paid')
    },
    totalPaid() {
      return this.paidOnly.reduce((s, p) => s + (Number(p.amount) || 0), 0)
    },
    totalOutstanding() {
      return this.payments
        .filter(p => !['paid', 'refunded'].includes((p.status || '').toLowerCase()))
        .reduce((s, p) => s + (Number(p.amount) || 0), 0)
    },
    paidCount() {
      return this.paidOnly.length
    },
    outstandingCount() {
      return this.payments.filter(p => 
        !['paid', 'refunded'].includes((p.status || '').toLowerCase())
      ).length
    },
    lastPaymentDate() {
      const arr = this.paidOnly
        .slice()
        .sort((a, b) => this.tsMs(b.timestamp) - this.tsMs(a.timestamp))
      return arr[0]?.timestamp || null
    },
    lastAmounts() {
      const arr = this.payments
        .slice()
        .sort((a, b) => this.tsMs(a.timestamp) - this.tsMs(b.timestamp))
      const vals = arr.slice(-12).map(p => Number(p.amount) || 0)
      return vals.length ? vals : [0]
    },
    sparklinePoints() {
      const vals = this.lastAmounts
      if (!vals.length) return '0,40 200,40'
      const max = Math.max(...vals, 1)
      const points = vals.map((v, i) => {
        const x = (i / (vals.length - 1)) * 200
        const y = 40 - (v / max) * 30
        return `${x},${y}`
      })
      return points.join(' ')
    },
    filteredPayments() {
      const s = (this.statusFilter || '').toLowerCase()
      const start = this.startDate ? new Date(this.startDate) : null
      const end = this.endDate ? new Date(this.endDate) : null
      if (end) end.setHours(23, 59, 59, 999)

      return this.payments.filter(p => {
        const okStatus = !s || (p.status || '').toLowerCase() === s
        const t = this.tsMs(p.timestamp)
        const okStart = !start || t >= start.getTime()
        const okEnd = !end || t <= end.getTime()
        return okStatus && okStart && okEnd
      })
    }
  },
  async mounted() {
    const user = auth.currentUser
    if (!user) {
      router.push('/')
      return
    }

    try {
      const q = query(
        collection(db, 'staffPayments'),
        where('staffUid', '==', user.uid)
      )
      const qs = await getDocs(q)
      this.payments = qs.docs.map(d => ({ id: d.id, ...d.data() }))
    } catch (e) {
      console.error('Error fetching payment history:', e)
      this.message = '❌ Failed to load payment history'
      this.messageType = 'error'
    } finally {
      this.loading = false
    }
  },
  methods: {
    money(n) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD'
      }).format(Number(n || 0))
    },
    tsMs(ts) {
      if (!ts) return 0
      if (ts.seconds) return ts.seconds * 1000
      if (ts.toDate) return ts.toDate().getTime()
      return new Date(ts).getTime()
    },
    formatDate(timestamp) {
      const ms = this.tsMs(timestamp)
      if (!ms) return ''
      const d = new Date(ms)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString()
    },
    prettyStatus(s = '') {
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
    exportCSV() {
      const rows = [
        ['Amount', 'Status', 'Date', 'Invoice URL'],
        ...this.filteredPayments.map(p => [
          (Number(p.amount) || 0).toFixed(2),
          this.prettyStatus(p.status || ''),
          this.formatDate(p.timestamp),
          p.invoiceUrl || ''
        ])
      ]
      const csv = rows
        .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    goBack() {
      router.push('/staff-dashboard')
    }
  }
}
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

  /* ==================== PAGE BACKGROUND ==================== */
  .payments-page {
    min-height: 100vh;
    background: #FBF7EF;
    position: relative;
    overflow-x: hidden;
    font-family: 'Inter', sans-serif;
    padding-bottom: 80px;
  }

  .payments-page .header-title,
  .payments-page .kpi-value {
    font-family: 'Fraunces', serif;
    letter-spacing: -0.01em;
  }
  
  .page-grid {
    position: fixed;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(30, 55, 51, 0.06) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(30, 55, 51, 0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    animation: grid-flow 35s linear infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  @keyframes grid-flow {
    0% { transform: translate(0, 0); }
    100% { transform: translate(60px, 60px); }
  }
  
  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(140px);
    opacity: 0.3;
    animation: orb-float 18s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  .orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(228,201,137,0.5), transparent);
    top: -200px;
    left: -200px;
  }
  
  .orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(30,55,51,0.3), transparent);
    bottom: -150px;
    right: -150px;
    animation-delay: 6s;
  }
  
  @keyframes orb-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(50px, -50px) scale(1.1); }
    66% { transform: translate(-40px, 40px) scale(0.9); }
  }
  
  /* ==================== HEADER ==================== */
  .payments-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: linear-gradient(160deg, #1E3733 0%, #2F4F4A 55%, #142925 100%);
    border-bottom: 1px solid rgba(251, 247, 239, 0.12);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
    margin-bottom: 40px;
  }
  
  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
  
  .header-title {
    font-size: clamp(28px, 4vw, 42px);
    font-weight: 600;
    color: #FBF7EF;
    margin-bottom: 4px;
    letter-spacing: -0.01em;
    text-shadow: none;
  }
  
  .header-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.95);
  }
  
  .btn-back {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }
  
  .btn-back:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-2px);
  }
  
  /* ==================== CONTAINER ==================== */
  .payments-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    z-index: 2;
  }
  
  /* ==================== KPI GRID ==================== */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
  }
  
  .kpi-card {
    display: flex;
    gap: 20px;
    padding: 24px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  
  .kpi-paid {
    border: 2px solid rgba(63, 148, 99, 0.3);
  }
  
  .kpi-paid:hover {
    box-shadow: 0 8px 30px rgba(63, 148, 99, 0.2);
    transform: translateY(-4px);
  }
  
  .kpi-outstanding {
    border: 2px solid rgba(179, 85, 74, 0.3);
  }
  
  .kpi-outstanding:hover {
    box-shadow: 0 8px 30px rgba(179, 85, 74, 0.2);
    transform: translateY(-4px);
  }
  
  .kpi-last {
    border: 2px solid rgba(200, 155, 76, 0.3);
  }
  
  .kpi-last:hover {
    box-shadow: 0 8px 30px rgba(200, 155, 76, 0.2);
    transform: translateY(-4px);
  }
  
  .kpi-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .kpi-paid .kpi-icon {
    background: rgba(63, 148, 99, 0.15);
    color: #3F9463;
    box-shadow: 0 4px 15px rgba(63, 148, 99, 0.2);
  }
  
  .kpi-outstanding .kpi-icon {
    background: rgba(179, 85, 74, 0.15);
    color: #B3554A;
    box-shadow: 0 4px 15px rgba(179, 85, 74, 0.2);
  }
  
  .kpi-last .kpi-icon {
    background: rgba(200, 155, 76, 0.15);
    color: #C89B4C;
    box-shadow: 0 4px 15px rgba(200, 155, 76, 0.2);
  }
  
  .kpi-content {
    flex: 1;
  }
  
  .kpi-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #5B6663;
    margin-bottom: 8px;
  }
  
  .kpi-value {
    font-size: 36px;
    font-weight: 600;
    color: #26302C;
    line-height: 1;
    margin-bottom: 8px;
  }
  
  .kpi-value-small {
    font-size: 18px;
    font-weight: 700;
    color: #26302C;
    margin-bottom: 12px;
  }
  
  .kpi-meta {
    font-size: 13px;
    color: #5B6663;
  }
  
  .sparkline-container {
    width: 100%;
    height: 40px;
    margin: 12px 0 8px;
  }
  
  .sparkline-container svg {
    width: 100%;
    height: 100%;
  }
  
  /* ==================== TABLE CARD ==================== */
  .table-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
  
  .table-header {
    padding: 24px;
    border-bottom: 1px solid #EFE8D6;
    background: #FBF7EF;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }
  
  .table-title {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #26302C;
    font-size: 18px;
    font-weight: 700;
  }
  
  .table-title svg {
    color: #C89B4C;
  }
  
  .table-filters {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  
  .filter-group label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5B6663;
  }
  
  .filter-group select,
  .filter-group input {
    padding: 10px 12px;
    background: white;
    border: 2px solid #E4DCC9;
    border-radius: 8px;
    color: #26302C;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    outline: none;
    min-width: 160px;
    transition: all 0.3s ease;
  }
  
  .filter-group select:focus,
  .filter-group input:focus {
    background: white;
    border-color: #C89B4C;
    box-shadow: 0 0 0 4px rgba(200, 155, 76, 0.1);
  }
  
  .filter-group select option {
    background: white;
    color: #26302C;
  }
  
  .btn-export {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    border: none;
    border-radius: 8px;
    color: #1E3733;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(200, 155, 76, 0.3);
  }
  
  .btn-export:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(200, 155, 76, 0.5);
  }
  
  /* ==================== TABLE ==================== */
  .payments-table {
    padding: 24px;
  }
  
  .table-head {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1.5fr 100px;
    gap: 16px;
    padding: 12px 16px;
    margin-bottom: 8px;
  }
  
  .th {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #5B6663;
  }
  
  .table-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .table-row {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1.5fr 100px;
    gap: 16px;
    padding: 16px;
    background: #FBF7EF;
    border: 1px solid #EFE8D6;
    border-radius: 12px;
    transition: all 0.2s ease;
  }
  
  .table-row:hover {
    background: white;
    border-color: rgba(200, 155, 76, 0.3);
    transform: translateX(4px);
    box-shadow: 0 2px 10px rgba(200, 155, 76, 0.1);
  }
  
  .td {
    display: flex;
    align-items: center;
  }
  
  .amount-value {
    font-size: 16px;
    font-weight: 700;
    color: #26302C;
  }
  
  .status-badge {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
  }
  
  .status-paid {
    background: rgba(63, 148, 99, 0.15);
    color: #3F9463;
    border: 1px solid rgba(63, 148, 99, 0.3);
  }
  
  .status-pending {
    background: rgba(156, 122, 52, 0.15);
    color: #9C7A34;
    border: 1px solid rgba(156, 122, 52, 0.35);
  }
  
  .status-failed {
    background: rgba(179, 85, 74, 0.15);
    color: #B3554A;
    border: 1px solid rgba(179, 85, 74, 0.3);
  }
  
  .status-refunded {
    background: rgba(200, 155, 76, 0.15);
    color: #C89B4C;
    border: 1px solid rgba(200, 155, 76, 0.3);
  }
  
  .status-unpaid {
    background: rgba(91, 102, 99, 0.15);
    color: #5B6663;
    border: 1px solid rgba(91, 102, 99, 0.3);
  }
  
  .date-value {
    font-size: 14px;
    color: #5B6663;
  }
  
  .btn-invoice {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid #E4DCC9;
    border-radius: 8px;
    color: #C89B4C;
    transition: all 0.2s ease;
  }
  
  .btn-invoice:hover {
    background: #FBF7EF;
    border-color: #C89B4C;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(200, 155, 76, 0.2);
  }
  
  .no-invoice {
    color: #5B6663;
    font-size: 14px;
  }
  
  /* ==================== LOADING STATE ==================== */
  .table-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .loader {
    width: 50px;
    height: 50px;
    border: 4px solid #EFE8D6;
    border-top-color: #C89B4C;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .table-loading p {
    color: #5B6663;
    font-size: 16px;
  }
  
  /* ==================== EMPTY STATE ==================== */
  .table-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .table-empty svg {
    color: #E4DCC9;
    margin-bottom: 20px;
  }
  
  .table-empty h3 {
    font-size: 20px;
    font-weight: 700;
    color: #26302C;
    margin-bottom: 8px;
  }
  
  .table-empty p {
    color: #5B6663;
    font-size: 16px;
  }
  
  /* ==================== MESSAGE ALERT ==================== */
  .message-alert {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    font-size: 14px;
    margin: 24px;
    animation: fade-in 0.3s ease;
  }
  
  .message-alert.success {
    background: rgba(63, 148, 99, 0.15);
    border: 2px solid #3F9463;
    color: #3F9463;
  }
  
  .message-alert.error {
    background: rgba(179, 85, 74, 0.15);
    border: 2px solid #B3554A;
    color: #8f4238;
  }
  
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 1024px) {
    .kpi-grid {
      grid-template-columns: 1fr;
    }
  
    .table-header {
      flex-direction: column;
      align-items: flex-start;
    }
  
    .table-filters {
      width: 100%;
      flex-direction: column;
    }
  
    .filter-group select,
    .filter-group input {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
    }
  
    .btn-back {
      width: 100%;
      justify-content: center;
    }
  
    .table-head,
    .table-row {
      grid-template-columns: 1fr;
      gap: 12px;
    }
  
    .th {
      display: none;
    }
  
    .td {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }
  
    .td::before {
      content: attr(data-label);
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #5B6663;
    }
  }
  
  @media (max-width: 600px) {
    .payments-container {
      padding: 0 16px;
    }
  
    .kpi-card {
      flex-direction: column;
      text-align: center;
    }
  
    .kpi-icon {
      margin: 0 auto;
    }
  }
  </style>